import { AnyAction } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { action } from "typesafe-actions";

import api from "../../../api";
import { AdvertiserType } from "../../../types/advertiserTypes";
import { AgencyType } from "../../../types/agencyTypes";
import { CURRENT_BRANDGEN_ADVERTISER_ID_STORAGE_KEY } from "../../../constants/storageKeys";
import { PlayBook } from "../../../types/playBookTypes";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { SET_ADVERTISERS, SET_AGENCY, SET_PLAYBOOKS, SET_SELECTED_ADVERTISER, SetUpdatedAgencyAction } from "./types";
import { handleApiResponse } from "../../../utils/handleApiResponse";
import { setApiError } from "../apiError/actions";
import { setCampaignToCreateSelectedAdvertiser } from "../campaignToCreate/actions";

export const setAgency = (agency: AgencyType): SetUpdatedAgencyAction => action(SET_AGENCY, agency);

export const setAdvertisers = (payload: AdvertiserType[]) => action(SET_ADVERTISERS, payload);

export const setPlayBooks = (payload: PlayBook[]) => action(SET_PLAYBOOKS, payload);

export const setSelectedAdvertiserId = (advertiserId: string | null) => action(SET_SELECTED_ADVERTISER, advertiserId);

export const selectGlobalAdvertiser =
  (advertiser: AdvertiserType): ThunkAction<void, ReduxApplicationState, {}, AnyAction> =>
  (dispatch) => {
    dispatch(setSelectedAdvertiserId(advertiser.brandgenAdvertiserId));
    dispatch(setCampaignToCreateSelectedAdvertiser(advertiser));
    localStorage.setItem(CURRENT_BRANDGEN_ADVERTISER_ID_STORAGE_KEY, advertiser.brandgenAdvertiserId);
  };

export const initializeGlobalAdvertiser =
  (): ThunkAction<void, ReduxApplicationState, {}, AnyAction> => (dispatch, getState) => {
    const { agency, campaignToCreate } = getState();
    const { advertisers, selectedAdvertiserId } = agency;

    if (!advertisers.length) return;

    const resolveAdvertiser = (id: string | null | undefined): AdvertiserType | undefined => {
      if (!id) return undefined;
      return advertisers.find((advertiser) => advertiser.brandgenAdvertiserId === id);
    };

    const existingCampaignAdvertiser = resolveAdvertiser(campaignToCreate.selectedAdvertiser?.brandgenAdvertiserId);
    if (existingCampaignAdvertiser) {
      if (selectedAdvertiserId !== existingCampaignAdvertiser.brandgenAdvertiserId) {
        dispatch(setSelectedAdvertiserId(existingCampaignAdvertiser.brandgenAdvertiserId));
      }
      return;
    }

    const existingSelectedAdvertiser = resolveAdvertiser(selectedAdvertiserId);
    if (existingSelectedAdvertiser) {
      dispatch(setCampaignToCreateSelectedAdvertiser(existingSelectedAdvertiser));
      localStorage.setItem(CURRENT_BRANDGEN_ADVERTISER_ID_STORAGE_KEY, existingSelectedAdvertiser.brandgenAdvertiserId);
      return;
    }

    const storedId = localStorage.getItem(CURRENT_BRANDGEN_ADVERTISER_ID_STORAGE_KEY);
    const advertiser = resolveAdvertiser(storedId) ?? advertisers[0];
    dispatch(selectGlobalAdvertiser(advertiser));
  };

export const createAgency =
  (agency: Partial<AgencyType>): ThunkAction<Promise<void>, {}, {}, AnyAction> =>
  async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
    const selectedAgency = await api.createBrandgenAgency(agency);

    dispatch(handleApiResponse(selectedAgency, setAgency, setApiError));
  };

export const setAgencyAdvertisers = (
  agencyId: string,
): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>): Promise<void> => {
    const advertisersByAgency = await api.fetchAdvertisersByAgency(agencyId);
    dispatch(handleApiResponse(advertisersByAgency, setAdvertisers, setApiError));
    dispatch(initializeGlobalAdvertiser());
  };
};

export const setBrandgenPlayBooks = (): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
    const playBooks = await api.getPlayBooks();
    dispatch(handleApiResponse(playBooks, setPlayBooks, setApiError));
  };
};
